import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    n: 1, title: "Information We Collect",
    body: (
      <>
        <h3 className="text-white font-bold mt-2">Information Automatically Collected from Discord</h3>
        <p>When you use ModNode on Discord, we automatically collect and store:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Discord User IDs</li>
          <li>Discord Server (Guild) IDs</li>
          <li>Discord Channel IDs</li>
          <li>Discord Role IDs</li>
          <li>Message content (only when required for specific features, such as message filtering or moderation)</li>
          <li>User nicknames and usernames</li>
          <li>User avatar information</li>
          <li>Timestamps of actions and commands</li>
          <li>Moderation records (warnings, mutes, bans, timeouts)</li>
          <li>Configuration settings for your server</li>
          <li>Command usage logs</li>
          <li>Bot permission levels and role assignments</li>
        </ul>
        <h3 className="text-white font-bold mt-4">Information You Provide</h3>
        <p>When you interact with ModNode or contact our support team, you may provide:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Support tickets and inquiries</li>
          <li>Feedback and suggestions</li>
          <li>Configuration preferences</li>
          <li>Custom settings and rules</li>
        </ul>
      </>
    ),
  },
  {
    n: 2, title: "How We Use Your Information",
    body: (
      <>
        <p>ModNode uses collected information for:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><span className="text-white font-semibold">Providing Services</span> — Operating the bot, processing commands, and delivering moderation features</li>
          <li><span className="text-white font-semibold">Moderation</span> — Enforcing server rules, logging actions, and maintaining community safety</li>
          <li><span className="text-white font-semibold">Customization</span> — Remembering your server's settings and preferences</li>
          <li><span className="text-white font-semibold">Analytics</span> — Understanding usage patterns to improve features and performance</li>
          <li><span className="text-white font-semibold">Support</span> — Responding to inquiries and troubleshooting issues</li>
          <li><span className="text-white font-semibold">Security</span> — Detecting and preventing abuse, fraud, and unauthorized access</li>
          <li><span className="text-white font-semibold">Legal Compliance</span> — Meeting legal obligations and enforcing our Terms of Service</li>
        </ul>
      </>
    ),
  },
  {
    n: 3, title: "Data Retention",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1">
          <li><span className="text-white font-semibold">Moderation Records</span> — Retained for the duration your server uses ModNode, plus 90 days after removal</li>
          <li><span className="text-white font-semibold">Configuration Settings</span> — Retained while ModNode is active in your server</li>
          <li><span className="text-white font-semibold">User Data</span> — Deleted when the bot is removed from a server, except where retention is legally required</li>
          <li><span className="text-white font-semibold">Logs</span> — Server logs retained for 30 days for security and troubleshooting purposes</li>
        </ul>
        <p>You may request deletion of your data at any time by contacting our support team.</p>
      </>
    ),
  },
  {
    n: 4, title: "Data Security",
    body: (
      <>
        <p>We implement technical and organizational measures to protect your data, including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Encrypted data transmission (HTTPS)</li>
          <li>Secure database access controls</li>
          <li>Regular security audits</li>
          <li>Limited employee access to personal data</li>
        </ul>
        <p>However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your information.</p>
      </>
    ),
  },
  {
    n: 5, title: "Sharing Your Information",
    body: (
      <>
        <p>ModNode does not sell, trade, or rent your personal information. We may share data only in these circumstances:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><span className="text-white font-semibold">With Discord</span> — As required by Discord's API and Terms of Service</li>
          <li><span className="text-white font-semibold">Service Providers</span> — Third-party services that help us operate ModNode (hosting, analytics, databases)</li>
          <li><span className="text-white font-semibold">Legal Requirements</span> — When required by law or to protect our rights and safety</li>
          <li><span className="text-white font-semibold">Server Owners</span> — Moderation records are visible to authorized server staff</li>
        </ul>
      </>
    ),
  },
  {
    n: 6, title: "Third-Party Services",
    body: (
      <>
        <p>ModNode may use third-party services and APIs, including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Discord API</li>
          <li>Cloud hosting providers</li>
          <li>Analytics services</li>
          <li>Database providers</li>
        </ul>
        <p>These third parties have their own privacy policies. ModNode is not responsible for their data practices. We encourage you to review their privacy policies.</p>
      </>
    ),
  },
  {
    n: 7, title: "Your Rights",
    body: (
      <>
        <p>Depending on your location, you may have rights including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><span className="text-white font-semibold">Right to Access</span> — Request what data we hold about you</li>
          <li><span className="text-white font-semibold">Right to Correction</span> — Request corrections to inaccurate data</li>
          <li><span className="text-white font-semibold">Right to Deletion</span> — Request deletion of your data (subject to legal requirements)</li>
          <li><span className="text-white font-semibold">Right to Opt-Out</span> — Opt out of data collection by removing ModNode from your server</li>
          <li><span className="text-white font-semibold">Data Portability</span> — Request your data in a portable format</li>
        </ul>
        <p>
          To exercise these rights, contact our support team at{" "}
          <a href="https://discord.gg/ModNode" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline" data-testid="privacy-support-link">
            discord.gg/ModNode
          </a>
        </p>
      </>
    ),
  },
  {
    n: 8, title: "Children's Privacy",
    body: (
      <>
        <p>ModNode is not directed to children under 13. We do not knowingly collect information from children under 13. If we become aware of such collection, we will delete the information and terminate the child's access.</p>
        <p>Discord's Terms of Service require users to be at least 13 years old.</p>
      </>
    ),
  },
  {
    n: 9, title: "Cookies and Tracking",
    body: <p>ModNode does not use cookies or tracking technologies on its website or bot. The ModNode website may use analytics cookies to understand usage patterns. You can control cookies through your browser settings.</p>,
  },
  {
    n: 10, title: "International Data Transfers",
    body: <p>Your information may be stored and processed in the United States and other countries. By using ModNode, you consent to the transfer of your information outside your country of residence, which may have different data protection rules.</p>,
  },
  {
    n: 11, title: "Changes to This Privacy Policy",
    body: (
      <>
        <p>ModNode may update this Privacy Policy at any time. We will notify users of significant changes via:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Updates to the policy on our website</li>
          <li>Announcements in our support server</li>
          <li>Other reasonable means</li>
        </ul>
        <p>Your continued use of ModNode after changes are made constitutes acceptance of the updated Privacy Policy.</p>
      </>
    ),
  },
  {
    n: 12, title: "Contact Us",
    body: (
      <>
        <p>For questions about this Privacy Policy, to request access to your data, or to exercise your privacy rights, please contact us:</p>
        <p>
          <span className="text-white font-semibold">ModNode Support Server:</span>{" "}
          <a href="https://discord.gg/ModNode" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">discord.gg/ModNode</a>
        </p>
        <p>Email inquiries can be directed through our Discord support server.</p>
      </>
    ),
  },
  {
    n: 13, title: "GDPR and Privacy Rights",
    body: (
      <>
        <p>If you are located in the European Union, United Kingdom, or other regions with similar privacy laws, you have additional rights under GDPR and similar regulations. ModNode complies with applicable privacy laws and your rights to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Request confirmation of data processing</li>
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion (right to be forgotten)</li>
          <li>Restrict processing</li>
          <li>Data portability</li>
          <li>Object to processing</li>
          <li>Lodge complaints with your data protection authority</li>
        </ul>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="privacy-page">
      {/* Top nav */}
      <div className="sticky top-0 z-20 bg-[#06070A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors" data-testid="privacy-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-xs tracking-widest uppercase">Back</span>
          </Link>
          <div className="flex items-center gap-2 text-[#94A3B8]">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
            <span className="font-display font-black tracking-tight">PRIVACY POLICY</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
            MOD<span className="text-[#8B5CF6]">NODE</span> · Privacy Policy
          </h1>
          <p className="text-sm text-[#64748B] font-mono">Last Updated: September 14, 2026</p>
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            ModNode Productions ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you use ModNode, our
            Discord bot, website, and related services.
          </p>
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do
            not use our services.
          </p>
        </header>

        {SECTIONS.map((s) => (
          <section key={s.n} className="space-y-3 border-t border-white/10 pt-6" data-testid={`privacy-section-${s.n}`}>
            <h2 className="font-display text-lg font-bold text-white flex items-baseline gap-3">
              <span className="text-[#10B981] font-mono text-sm">{String(s.n).padStart(2, "0")}</span>
              {s.title}
            </h2>
            <div className="text-sm text-[#E2E8F0] leading-relaxed space-y-3">
              {s.body}
            </div>
          </section>
        ))}

        <footer className="pt-8 border-t border-white/10 space-y-1">
          <p className="font-display font-black text-white">ModNode Productions</p>
          <p className="text-xs text-[#64748B] font-mono">Command your Discord like an operator.</p>
        </footer>
      </div>
    </div>
  );
}
